import { useEffect, useMemo, useState } from "react";
import { CalendarPlus, Check, MessageCircle, ReceiptText, RotateCcw, MapPin, UsersRound, CreditCard, Loader2 } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { guestApi } from "@/lib/api";
import { clearBookingDraft, getBookingDraft } from "@/lib/bookingDraft";

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(`${value}T12:00:00`);
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const formatDateTime = (value) => value
  ? new Date(value).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })
  : "—";

const money = (value, currency = "USD") => new Intl.NumberFormat("en-US", {
  style: "currency",
  currency,
}).format(Number(value || 0));

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const terminalStatuses = new Set(["confirmed", "refunded", "refund_pending", "cancelled"]);

export default function BookingConfirmed() {
  const [searchParams] = useSearchParams();
  const bookingIdFromUrl = searchParams.get("booking_id");
  const [draft] = useState(() => getBookingDraft());
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(Boolean(bookingIdFromUrl));

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!bookingIdFromUrl) {
        setLoading(false);
        return;
      }
      try {
        for (let attempt = 0; attempt < 5; attempt += 1) {
          const response = await guestApi.booking(bookingIdFromUrl);
          const value = response?.unwrapped ?? response?.data ?? response;
          if (cancelled) return;
          setBooking(value);
          if (terminalStatuses.has(value?.status)) break;
          if (attempt < 4) await sleep(1000);
        }
      } catch {
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [bookingIdFromUrl]);

  useEffect(() => {
    if (terminalStatuses.has(booking?.status)) clearBookingDraft();
  }, [booking]);

  const details = booking || draft || {};
  const bookingId = details.id || details.booking_id || bookingIdFromUrl;
  const confirmation = details.confirmation_number || details.reference || bookingId || "Processing";
  const propertyName = details.property?.title || details.property?.name || details.property_name || details.propertyName || "Your StayEasy stay";
  const location = details.property?.location || details.location || "";
  const image = details.property?.images?.[0]?.url || details.property?.images?.[0] || details.propertyImage || "";
  const checkIn = details.check_in || details.checkIn;
  const checkOut = details.check_out || details.checkOut;
  const guestName = details.guest?.name || details.guestName || "";
  const additionalGuests = details.guest?.additional_names || details.additionalGuestNames || [];
  const guestCount = Number(details.guests || 1);
  const total = Number(details.total_amount ?? details.total ?? 0);
  const currency = details.currency || "USD";
  const confirmed = booking?.status === "confirmed";
  const refunded = booking?.status === "refunded";
  const refundPending = booking?.status === "refund_pending";
  const cancelledBooking = booking?.status === "cancelled";
  const paymentRejected = refunded || refundPending || cancelledBooking;

  const calendarHref = useMemo(() => {
    if (!checkIn || !checkOut) return null;
    const start = checkIn.replaceAll("-", "");
    const end = checkOut.replaceAll("-", "");
    const text = encodeURIComponent(propertyName);
    const detailsText = encodeURIComponent(`StayEasy booking ${confirmation}`);
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${text}&dates=${start}/${end}&details=${detailsText}`;
  }, [checkIn, checkOut, propertyName, confirmation]);

  const eyebrow = confirmed ? "Booking confirmed" : paymentRejected ? "Booking not confirmed" : "Payment processing";
  const heading = confirmed
    ? "Thank you. Your stay is booked."
    : refunded
      ? "Your payment was refunded."
      : refundPending
        ? "Your refund is processing."
        : cancelledBooking
          ? "This booking was cancelled."
          : "We’re finalizing your stay.";

  return (
    <main className="mx-auto min-h-[75vh] max-w-[1120px] px-5 py-12 md:px-10 md:py-20">
      <div className="overflow-hidden border border-foreground/10 bg-card shadow-sm">
        <div className="grid lg:grid-cols-[1.05fr_0.95fr]">
          <section className="p-7 md:p-10 lg:p-12">
            <div className={`flex h-14 w-14 items-center justify-center rounded-full ${paymentRejected ? "bg-muted text-foreground" : "bg-primary text-primary-foreground"}`}>
              {loading ? <Loader2 size={25} className="animate-spin" /> : paymentRejected ? <RotateCcw size={26} /> : <Check size={28} />}
            </div>
            <p className="mt-7 text-xs uppercase tracking-[0.22em] text-muted-foreground">{eyebrow}</p>
            <h1 className="mt-3 max-w-2xl font-display text-5xl font-medium tracking-tight md:text-7xl">{heading}</h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground">
              {confirmed
                ? `Your reservation at ${propertyName} is confirmed. Keep this page or your booking reference handy for your stay.`
                : loading
                  ? "Stripe has returned you to StayEasy. We are waiting for the final payment confirmation."
                  : "Your booking status is shown below. You can always return to My Bookings for the latest information."}
            </p>

            <div className="mt-9 grid gap-3 sm:grid-cols-2">
              <Info label="Booking reference" value={confirmation} />
              <Info label="Booked" value={formatDateTime(details.created_at)} />
              <Info label="Check-in" value={formatDate(checkIn) || "—"} />
              <Info label="Check-out" value={formatDate(checkOut) || "—"} />
              <Info label="Guests" value={`${guestCount} guest${guestCount === 1 ? "" : "s"}`} />
              <Info label="Total paid" value={total ? money(total, currency) : "Processing"} />
            </div>

            {(guestName || additionalGuests.length > 0) && (
              <div className="mt-7 border-t border-foreground/10 pt-6">
                <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground"><UsersRound size={14} /> Guests</p>
                <p className="mt-3 font-medium">{guestName || "Primary guest"}</p>
                {additionalGuests.length > 0 && <p className="mt-1 text-sm text-muted-foreground">Also travelling: {additionalGuests.join(", ")}</p>}
              </div>
            )}

            <div className="mt-8 flex flex-wrap gap-3">
              {confirmed && bookingId && (
                <Link to={`/account/bookings/${bookingId}/messages`} className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground">
                  <MessageCircle size={16} /> Message host
                </Link>
              )}
              {confirmed && calendarHref && (
                <a href={calendarHref} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-foreground/15 px-6 py-3 text-sm font-medium">
                  <CalendarPlus size={16} /> Add to calendar
                </a>
              )}
              <Link to="/account/bookings" className="inline-flex items-center gap-2 rounded-full border border-foreground/15 px-6 py-3 text-sm font-medium">
                <ReceiptText size={16} /> My bookings
              </Link>
              {paymentRejected && <Link to="/properties" className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-medium text-primary-foreground">Find another stay</Link>}
            </div>
          </section>

          <aside className="border-t border-foreground/10 bg-muted/25 p-7 md:p-10 lg:border-l lg:border-t-0 lg:p-12">
            {image && <img src={image} alt="" className="h-64 w-full object-cover" />}
            <p className="mt-6 text-xs uppercase tracking-[0.18em] text-muted-foreground">Your stay</p>
            <h2 className="mt-2 font-display text-3xl leading-tight">{propertyName}</h2>
            {location && <p className="mt-3 flex items-center gap-2 text-sm text-muted-foreground"><MapPin size={14} /> {location}</p>}

            <div className="mt-7 space-y-4 border-t border-foreground/10 pt-6 text-sm">
              <div className="flex justify-between gap-4"><span className="text-muted-foreground">Payment status</span><strong className="font-medium capitalize">{booking?.status || "processing"}</strong></div>
              <div className="flex justify-between gap-4"><span className="text-muted-foreground">Transaction ID</span><strong className="max-w-[210px] break-all text-right font-mono text-xs">{details.transaction?.transaction_id || "—"}</strong></div>
              <div className="flex justify-between gap-4"><span className="text-muted-foreground">Stripe payment</span><strong className="max-w-[210px] break-all text-right font-mono text-xs">{details.stripe_payment_intent_id || "—"}</strong></div>
              <div className="flex justify-between gap-4"><span className="text-muted-foreground">Stripe checkout</span><strong className="max-w-[210px] break-all text-right font-mono text-xs">{details.stripe_checkout_session_id || searchParams.get("session_id") || "—"}</strong></div>
            </div>

            <div className="mt-7 flex gap-3 rounded-lg border border-foreground/10 bg-background/60 p-4 text-sm text-muted-foreground">
              <CreditCard size={18} className="mt-0.5 shrink-0 text-primary" />
              <p>Your payment record and reservation details remain available from My Bookings.</p>
            </div>
          </aside>
        </div>
      </div>
    </main>
  );
}

const Info = ({ label, value }) => (
  <div className="border border-foreground/10 bg-background/50 p-4">
    <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{label}</p>
    <p className="mt-2 text-sm font-medium">{value}</p>
  </div>
);
